import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static targets = ["phaseDots", "currentPhase", "completedItems", "whisperCard", "progress", "helpButton"]
  static values = {
    sessionId: Number,
    callId: Number
  }

  connect() {
    this.session = null
    this.subscription = null

    if (this.sessionIdValue) {
      this.loadSession()
      this.subscribeToChannel()
    }
  }

  disconnect() {
    this.unsubscribe()
  }

  async loadSession() {
    try {
      const response = await fetch(`/api/v1/chrome_extension/calls/${this.callIdValue}/coaching_session`, {
        headers: this.authHeaders()
      })
      const data = await response.json()
      if (data.coaching_session) {
        this.session = data.coaching_session
        this.render()
      }
    } catch (error) {
      console.error("[CoachingPanel] Failed to load session:", error)
    }
  }

  subscribeToChannel() {
    this.subscription = consumer.subscriptions.create(
      { channel: "CoachingSessionChannel", coaching_session_id: this.sessionIdValue },
      {
        received: (data) => this.handleBroadcast(data)
      }
    )
  }

  unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  handleBroadcast(data) {
    switch (data.type) {
      case "transcript":
        // Transcript updates are handled server-side for keyword detection
        break
      case "item_completed":
        this.handleItemCompleted(data)
        break
      case "whisper_card":
        this.showWhisperCard(data)
        break
    }
  }

  handleItemCompleted(data) {
    if (!this.session) return

    if (!this.session.completed_items.includes(data.item_id)) {
      this.session.completed_items.push(data.item_id)
    }
    this.session.completion_percentage = data.completion_percentage
    this.session.current_phase_index = data.current_phase_index
    this.render()
    this.flashCompletion()
  }

  render() {
    if (!this.session) return

    this.renderPhaseDots()
    this.renderCurrentPhase()
    this.renderCompletedItems()
    this.renderProgress()
  }

  renderPhaseDots() {
    if (!this.hasPhaseDotsTarget) return

    const { phases, current_phase_index } = this.session
    this.phaseDotsTarget.innerHTML = phases.map((phase, index) => {
      let stateClass = "phase-pending"
      if (index < current_phase_index) {
        stateClass = "phase-completed"
      } else if (index === current_phase_index) {
        stateClass = "phase-current"
      }

      return `
        <div class="phase-dot ${stateClass}" title="${phase.name}">
          <span class="phase-dot-indicator"></span>
          <span class="phase-dot-label">${phase.name}</span>
        </div>
      `
    }).join("")
  }

  renderCurrentPhase() {
    if (!this.hasCurrentPhaseTarget) return

    const { phases, completed_items, current_phase_index } = this.session
    const phase = phases[current_phase_index]
    if (!phase) return

    const items = phase.items || []
    const pendingItems = items.filter(item => !completed_items.includes(item.id))
    const completedCount = items.length - pendingItems.length
    const nextItem = pendingItems[0]

    let html = `
      <div class="phase-header">
        <span class="phase-name">${phase.name}</span>
        <span class="phase-count">${completedCount}/${items.length}</span>
      </div>
    `

    if (nextItem) {
      const criticalClass = nextItem.critical ? "item-critical" : ""
      html += `
        <div class="current-item ${criticalClass}" data-item-id="${nextItem.id}">
          <span class="item-marker">→</span>
          <span class="item-text">${nextItem.text}</span>
        </div>
      `
    } else {
      html += `<div class="phase-complete-msg">Phase complete!</div>`
    }

    this.currentPhaseTarget.innerHTML = html
  }

  renderCompletedItems() {
    if (!this.hasCompletedItemsTarget) return

    const { phases, completed_items } = this.session
    const allItems = phases.flatMap(phase => phase.items || [])
    const completed = allItems.filter(item => completed_items.includes(item.id))

    if (completed.length === 0) {
      this.completedItemsTarget.innerHTML = ""
      return
    }

    this.completedItemsTarget.innerHTML = `
      <div class="completed-items-header">Completed (${completed.length})</div>
      <div class="completed-items-list">
        ${completed.map(item => `
          <div class="completed-item" data-item-id="${item.id}" data-action="click->coaching-panel#uncompleteItem">
            <span class="item-check">✓</span>
            <span class="item-text">${item.text}</span>
          </div>
        `).join("")}
      </div>
    `
  }

  renderProgress() {
    if (!this.hasProgressTarget) return
    this.progressTarget.textContent = `${this.session.completion_percentage}%`
  }

  async completeCurrentItem() {
    const nextItem = this.getCurrentItem()
    if (!nextItem) return

    try {
      const response = await fetch(`/api/v1/chrome_extension/coaching_sessions/${this.session.id}/completed_items`, {
        method: "POST",
        headers: { ...this.authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ item_id: nextItem.id })
      })
      const data = await response.json()

      this.session.completed_items = data.completed_items
      this.session.completion_percentage = data.completion_percentage
      this.session.current_phase_index = data.current_phase_index
      this.render()
      this.flashCompletion()
    } catch (error) {
      console.error("[CoachingPanel] Failed to complete item:", error)
    }
  }

  async uncompleteItem(event) {
    const itemId = event.currentTarget.dataset.itemId
    if (!itemId) return

    try {
      const response = await fetch(`/api/v1/chrome_extension/coaching_sessions/${this.session.id}/completed_items/${itemId}`, {
        method: "DELETE",
        headers: this.authHeaders()
      })
      const data = await response.json()

      this.session.completed_items = data.completed_items
      this.session.completion_percentage = data.completion_percentage
      this.render()
    } catch (error) {
      console.error("[CoachingPanel] Failed to uncomplete item:", error)
    }
  }

  getCurrentItem() {
    const { phases, completed_items, current_phase_index } = this.session
    const phase = phases[current_phase_index]
    if (!phase) return null

    const items = phase.items || []
    return items.find(item => !completed_items.includes(item.id))
  }

  async requestHelp() {
    if (!this.hasHelpButtonTarget) return

    this.helpButtonTarget.disabled = true
    this.helpButtonTarget.textContent = "Getting suggestion..."

    try {
      const response = await fetch(`/api/v1/chrome_extension/coaching_sessions/${this.session.id}/ai_suggestions`, {
        method: "POST",
        headers: this.authHeaders()
      })
      const data = await response.json()

      if (data.ai_suggestion) {
        this.showWhisperCard({
          trigger: data.ai_suggestion.trigger || "manual",
          suggestion: data.ai_suggestion.text,
          timestamp: data.ai_suggestion.timestamp
        })
      } else if (data.error) {
        this.showWhisperCard({
          trigger: "error",
          suggestion: data.error,
          timestamp: new Date().toISOString()
        })
      }
    } catch (error) {
      console.error("[CoachingPanel] AI Help request failed:", error)
      this.showWhisperCard({
        trigger: "error",
        suggestion: "Unable to get AI suggestion. Please try again.",
        timestamp: new Date().toISOString()
      })
    } finally {
      this.helpButtonTarget.disabled = false
      this.helpButtonTarget.textContent = "Ask AI for Help"
    }
  }

  showWhisperCard(data) {
    if (!this.hasWhisperCardTarget) return

    const triggerClass = `whisper-${data.trigger}`

    this.whisperCardTarget.innerHTML = `
      <div class="whisper-card ${triggerClass}">
        <div class="whisper-header">
          <span class="whisper-label">AI SUGGESTION</span>
          <button class="whisper-dismiss" data-action="coaching-panel#dismissWhisperCard">×</button>
        </div>
        <div class="whisper-content">
          ${data.suggestion}
        </div>
        <div class="whisper-actions">
          <button class="whisper-btn whisper-btn-use" data-action="coaching-panel#markSuggestionUsed" data-timestamp="${data.timestamp}">Use This</button>
          <button class="whisper-btn whisper-btn-not-helpful" data-action="coaching-panel#markSuggestionNotHelpful" data-timestamp="${data.timestamp}">Not Helpful</button>
        </div>
      </div>
    `

    // Auto-dismiss after 15 seconds
    this.whisperDismissTimer = setTimeout(() => this.dismissWhisperCard(), 15000)
  }

  dismissWhisperCard() {
    if (this.whisperDismissTimer) {
      clearTimeout(this.whisperDismissTimer)
    }
    if (this.hasWhisperCardTarget) {
      this.whisperCardTarget.innerHTML = ""
    }
  }

  async markSuggestionUsed(event) {
    await this.submitSuggestionFeedback(event.currentTarget.dataset.timestamp, true)
    this.dismissWhisperCard()
  }

  async markSuggestionNotHelpful(event) {
    await this.submitSuggestionFeedback(event.currentTarget.dataset.timestamp, false)
    this.dismissWhisperCard()
  }

  async submitSuggestionFeedback(timestamp, used) {
    try {
      await fetch(`/api/v1/chrome_extension/coaching_sessions/${this.session.id}/ai_suggestion_feedback`, {
        method: "POST",
        headers: { ...this.authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ timestamp, used })
      })
    } catch (error) {
      console.error("[CoachingPanel] Failed to submit feedback:", error)
    }
  }

  flashCompletion() {
    const currentItem = this.currentPhaseTarget?.querySelector(".current-item")
    if (currentItem) {
      currentItem.classList.add("item-flash")
      setTimeout(() => currentItem.classList.remove("item-flash"), 500)
    }
  }

  authHeaders() {
    // Get API token from meta tag or data attribute
    const token = document.querySelector('meta[name="api-token"]')?.content ||
                  this.element.dataset.apiToken
    return {
      "Authorization": `Bearer ${token}`
    }
  }
}
